import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  alpha,
} from "@mui/material";
import {
  Timeline as TimelineIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as PendingIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

const StyledCard = styled(Card)(({ theme }) => ({
  background: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: "blur(10px)",
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[10],
}));

const MilestoneCard = styled(Card)<{ completed?: boolean }>(({ theme, completed }) => ({
  background: completed
    ? `linear-gradient(45deg, ${alpha(theme.palette.success.main, 0.1)} 30%, ${alpha(theme.palette.success.main, 0.2)} 90%)`
    : alpha(theme.palette.background.paper, 0.9),
  border: completed ? `2px solid ${theme.palette.success.main}` : `2px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.spacing(2),
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: theme.shadows[10],
  },
}));

interface MilestoneTask {
  id: number;
  name: string;
  completed: boolean;
}

interface Milestone {
  id: number;
  title: string;
  description: string;
  targetDay: number;
  completed: boolean;
  completedDate: string | null;
  tasks: MilestoneTask[];
}

const MilestonesTab: React.FC = () => {
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentDay, setCurrentDay] = useState(0);
  const [overallProgress, setOverallProgress] = useState(0);

  useEffect(() => {
    const userStr = sessionStorage.getItem("user");
    if (userStr) {
      const user = JSON.parse(userStr);
      fetchMilestones(user.id);
      const interval = setInterval(() => fetchMilestones(user.id), 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, []);

  const fetchMilestones = async (userId: number) => {
    try {
      const [milestonesResponse, summaryResponse] = await Promise.all([
        axios.get(`/api/milestones/employee/${userId}`),
        axios.get(`/api/milestones/summary/${userId}`)
      ]);

      setMilestones(milestonesResponse.data);
      setCurrentDay(summaryResponse.data.daysEmployed || 0);
      setOverallProgress(summaryResponse.data.progress || 0);
    } catch (error) {
      console.error("Error fetching milestones:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTask = async (taskId: number, completed: boolean) => {
    try {
      await axios.patch(`/api/milestones/tasks/${taskId}/complete?completed=${!completed}`);
      const userStr = sessionStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        fetchMilestones(user.id);
      }
    } catch (error) {
      console.error("Error updating task:", error);
    }
  };

  const getProgressForMilestone = (milestone: any) => {
    if (milestone.completed) return 100;
    if (currentDay >= milestone.targetDay) return 100;
    return Math.min((currentDay / milestone.targetDay) * 100, 100);
  };

  const completedTasks = milestones.reduce((acc, milestone) => {
    return acc + milestone.tasks.filter(task => task.completed).length;
  }, 0);

  const totalTasks = milestones.reduce((acc, milestone) => acc + milestone.tasks.length, 0);

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <Typography variant="body1" color="text.secondary">
          Loading your milestones...
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      {/* Progress Summary */}
      <Grid item xs={12}>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
              <TimelineIcon color="primary" sx={{ mr: 1 }} />
              Your Journey Progress
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box textAlign="center">
                  <Typography variant="h3" color="primary" gutterBottom>
                    {currentDay}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Days with us
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box textAlign="center">
                  <Typography variant="h3" color="success.main" gutterBottom>
                    {completedTasks}/{totalTasks}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tasks Completed
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box textAlign="center">
                  <Typography variant="h3" color="info.main" gutterBottom>
                    {overallProgress}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Overall Progress
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Box mt={3}>
              <Typography variant="body2" gutterBottom>
                Overall Journey Progress
              </Typography>
              <LinearProgress
                variant="determinate"
                value={overallProgress}
                sx={{ height: 10, borderRadius: 5 }}
              />
            </Box>
          </CardContent>
        </StyledCard>
      </Grid>

      {/* Milestones */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
          <StarIcon color="primary" sx={{ mr: 1 }} />
          Milestones Timeline
        </Typography>
        <Grid container spacing={2}>
          {milestones.map((milestone, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <MilestoneCard completed={milestone.completed}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    {milestone.completed ? (
                      <CheckIcon color="success" sx={{ mr: 1 }} />
                    ) : (
                      <PendingIcon color="action" sx={{ mr: 1 }} />
                    )}
                    <Typography variant="h6">
                      {milestone.title}
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {milestone.description}
                  </Typography>

                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="caption" color="text.secondary">
                      Target: Day {milestone.targetDay}
                    </Typography>
                    {milestone.completed && (
                      <Chip
                        label="Completed"
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>

                  {milestone.completedDate && (
                    <Typography variant="caption" color="success.main" gutterBottom display="block">
                      Completed: {milestone.completedDate}
                    </Typography>
                  )}

                  <Box mb={2}>
                    <Typography variant="body2" gutterBottom>
                      Progress ({Math.round(getProgressForMilestone(milestone))}%)
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={getProgressForMilestone(milestone)}
                      color={milestone.completed ? "success" : "primary"}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>

                  <Typography variant="body2" gutterBottom>
                    <strong>Tasks:</strong>
                  </Typography>
                  {milestone.tasks.map((task, taskIndex) => (
                    <Box
                      key={taskIndex}
                      display="flex"
                      alignItems="center"
                      mb={0.5}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { opacity: 0.7 }
                      }}
                      onClick={() => handleToggleTask(task.id, task.completed)}
                    >
                      {task.completed ? (
                        <CheckIcon color="success" sx={{ fontSize: 16, mr: 1 }} />
                      ) : (
                        <PendingIcon color="action" sx={{ fontSize: 16, mr: 1 }} />
                      )}
                      <Typography
                        variant="caption"
                        color={task.completed ? "text.primary" : "text.secondary"}
                        sx={{
                          textDecoration: task.completed ? "line-through" : "none",
                        }}
                      >
                        {task.name}
                      </Typography>
                    </Box>
                  ))}
                </CardContent>
              </MilestoneCard>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default MilestonesTab;